/**
 * Answering an agent's question from the composer strip (RFC 0304 3h, S4).
 *
 * The strip exists so a whole exchange happens without opening the thread. A
 * question is part of that exchange, so the strip answers every shape the
 * thread's form does: several questions paged one at a time, a multi-select,
 * and a typed answer. Typing is the one that mattered most: a sentence typed
 * into the strip while the agent waited used to be queued as a new message
 * behind the question it was answering.
 *
 * PURE except `answerPendingWithText`, so which question is on screen and what
 * a tap writes are testable without a rendered strip.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import {
  type InteractionAnswerValue,
  parseQuestionBody,
  projectSessionPendingInteractions,
  type QuestionBody,
} from '@mod/core/model'
import { answerInteraction } from '@mod/core/run'
import {
  isAnswered,
  isComplete,
  type Selection,
  toAnswers,
} from './QuestionForm'

const RECOMMENDED = /\s*\(recommended\)\s*$/i

/**
 * An option's label with the agent's pick marked rather than spelled out.
 *
 * The ask tool tells the model to put its pick first and end the label with
 * "(Recommended)". In a row of taps that suffix is most of the width, so the
 * strip draws the pick as the filled button and drops the words.
 */
export function pickLabel(label: string): {
  label: string
  recommended: boolean
} {
  if (!RECOMMENDED.test(label)) return { label, recommended: false }
  return { label: label.replace(RECOMMENDED, ''), recommended: true }
}

/** The first question without an answer, or the last one when all have one. */
export function currentStep(body: QuestionBody, selection: Selection): number {
  const i = body.questions.findIndex((q) => !isAnswered(selection[q.id]))
  return i === -1 ? body.questions.length - 1 : i
}

/** Where the strip is in a question: what was tapped, and which is showing. */
export interface StripTaps {
  selection: Selection
  step: number
}

export const NO_TAPS: StripTaps = { selection: {}, step: 0 }

/**
 * One tap, or the Next/Send button (`value` null), applied.
 *
 * Returns the answers when that completed the ask. A single choice is one tap
 * and moves on; a multi-select toggles and waits for the button, because
 * moving on at the first toggle would make a second pick impossible. A send
 * with a question skipped goes back to it rather than sending a partial answer.
 */
export function stripTap(
  body: QuestionBody,
  taps: StripTaps,
  value: string | null,
): { taps: StripTaps; answers: Record<string, InteractionAnswerValue> | null } {
  const question = body.questions[taps.step]
  if (!question) return { taps, answers: null }
  const last = taps.step === body.questions.length - 1
  const state = taps.selection[question.id] ?? { values: [], freeText: '' }
  let selection = taps.selection
  let advance = value === null
  if (value !== null) {
    const values = question.multiSelect
      ? state.values.includes(value)
        ? state.values.filter((v) => v !== value)
        : [...state.values, value]
      : [value]
    selection = { ...selection, [question.id]: { values, freeText: '' } }
    advance = !question.multiSelect
  }
  if (!advance) return { taps: { selection, step: taps.step }, answers: null }
  if (!isAnswered(selection[question.id]))
    return { taps: { selection, step: taps.step }, answers: null }
  if (!last) return { taps: { selection, step: taps.step + 1 }, answers: null }
  if (!isComplete(body, selection))
    return {
      taps: { selection, step: currentStep(body, selection) },
      answers: null,
    }
  return {
    taps: { selection, step: taps.step },
    answers: toAnswers(body, selection),
  }
}

/**
 * A typed sentence as the answer, or null when it cannot be one.
 *
 * ONE QUESTION ONLY. With several, the text answers one of them and the rest
 * would reach the model as silently dropped (`isComplete` explains why the
 * form refuses that), so the sentence goes as a message instead.
 */
export function textAnswer(
  body: QuestionBody,
  text: string,
): Record<string, InteractionAnswerValue> | null {
  const trimmed = text.trim()
  const only = body.questions.length === 1 ? body.questions[0] : undefined
  if (!only || !trimmed) return null
  return { [only.id]: { selected: [], freeText: trimmed } }
}

/**
 * Answer the session's pending question with what was typed, if it can take
 * it. True when the text was spent as the answer, so the caller does not also
 * send it as a message.
 */
export async function answerPendingWithText(
  repo: Repo,
  /** Absent for a session in no project: the World agent (RFC 0336). */
  projectId: string | undefined,
  sessionId: string,
  text: string,
): Promise<boolean> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const pending = projectSessionPendingInteractions(handle.doc()).find(
    (i) => i.kind === 'question',
  )
  if (!pending) return false
  const answers = textAnswer(parseQuestionBody(pending.bodyJson), text)
  if (!answers) return false
  return answerInteraction(
    repo,
    sessionId,
    pending.interactionId,
    answers,
    projectId ? { projectId } : {},
  )
}
